import { useEffect, useRef, useState } from "react";

import { Pager } from "../../../components/admin/Pager";
import {
  api,
  apiErrorDetail,
  JIRA_OUTCOMES,
  type AdminJiraLogEntry,
  type AdminJiraQueryFilters,
} from "../../../lib/api";
import { JiraQueryResults } from "./JiraQueryResults";

const LIMIT = 50;
/** `admin/jira_admin.py` rejects `offset` above 1000 (it reads limit+offset rows). */
const MAX_OFFSET = 1000;
/** Outcomes a jira_log row can carry: `unavailable` exits write no row (service.py). */
const LOGGED_OUTCOMES = JIRA_OUTCOMES.filter((o) => o !== "unavailable");
type Range = "any" | "24h" | "7d" | "30d";

/** Raw filter-bar state before it becomes API query params. */
export type FilterInput = {
  outcome: string;
  actor: string;
  slug: string;
  range: Range;
};

/**
 * Trim the filter bar into API filters, omitting blanks and the "any" range.
 * Actor and slug are lowercased: jira_log stores both that way (slugs are minted
 * lowercase) and the filters are exact-match.
 */
export function toQueryFilters(input: FilterInput): AdminJiraQueryFilters {
  const filters: AdminJiraQueryFilters = {};
  if (input.outcome) filters.outcome = input.outcome;
  if (input.actor.trim()) filters.actor = input.actor.trim().toLowerCase();
  if (input.slug.trim()) filters.slug = input.slug.trim().toLowerCase();
  if (input.range !== "any") filters.range = input.range;
  return filters;
}

/** Debounce a string value by `ms`. */
function useDebounced(value: string, ms: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

/** Recent Jira proxy calls with server-side filters and a detail drawer. */
export function JiraQueriesTab() {
  const [items, setItems] = useState<AdminJiraLogEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [outcome, setOutcome] = useState("");
  const [actor, setActor] = useState("");
  const [slug, setSlug] = useState("");
  const [range, setRange] = useState<Range>("any");
  const actorDebounced = useDebounced(actor, 400);
  const slugDebounced = useDebounced(slug, 400);
  const generation = useRef(0);
  const [reload, setReload] = useState(0); // bumped by Retry to refetch

  useEffect(
    () => setOffset(0),
    [outcome, actorDebounced, slugDebounced, range],
  );

  useEffect(() => {
    const gen = ++generation.current;
    setItems(null);
    setError(null);
    api.admin.jira
      .queries(
        LIMIT,
        offset,
        toQueryFilters({
          outcome,
          actor: actorDebounced,
          slug: slugDebounced,
          range,
        }),
      )
      .then((r) => {
        if (gen === generation.current) setItems(r.items);
      })
      .catch((e: unknown) => {
        if (gen === generation.current) setError(apiErrorDetail(e));
      });
  }, [offset, outcome, actorDebounced, slugDebounced, range, reload]);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select
          aria-label="Outcome"
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
          className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
        >
          <option value="">All outcomes</option>
          {LOGGED_OUTCOMES.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
        <input
          aria-label="Actor email"
          value={actor}
          onChange={(e) => setActor(e.target.value)}
          placeholder="Actor email…"
          className="w-48 rounded-md border border-slate-300 px-2 py-1.5 text-sm"
        />
        <input
          aria-label="Artifact slug"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="Slug…"
          className="w-28 rounded-md border border-slate-300 px-2 py-1.5 font-mono text-sm"
        />
        {(["any", "24h", "7d", "30d"] as const).map((r) => (
          <button
            key={r}
            type="button"
            aria-pressed={range === r}
            onClick={() => setRange(r)}
            className={`rounded-md border px-2.5 py-1.5 text-sm ${range === r ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
          >
            {r === "any" ? "Any time" : r}
          </button>
        ))}
      </div>
      {error && (
        <p className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
          {error}{" "}
          <button
            type="button"
            onClick={() => setReload((n) => n + 1)}
            className="underline"
          >
            Retry
          </button>
        </p>
      )}
      {items === null ? (
        !error && <p className="text-slate-500">Loading…</p>
      ) : (
        <>
          <JiraQueryResults items={items} />
          <Pager
            offset={offset}
            limit={LIMIT}
            count={items.length}
            maxOffset={MAX_OFFSET}
            onChange={setOffset}
          />
        </>
      )}
    </div>
  );
}
